import { get } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import styled from 'styled-components'

import { colors } from 'app/theme/colors'
import { radius, shadow, space } from 'app/theme/tokens'

/**
 * Mobile drop-in for the horizontal-scrolling mui-datatables that BaseTable
 * renders on desktop. Same columns + data shape; rendered as a vertical
 * list of cards with client-side search.
 *
 *   <MobileEntityCardList
 *     data={data}           // { data: [], isLoaded, isLoading, count, ... }
 *     columns={columns}     // the same column defs BaseTable consumes
 *     label="Subjects"      // used for the search placeholder + empty state
 *   />
 *
 * What it shows per row:
 *   · the first up-to-4 visible columns (`options.display !== false`) as
 *     label/value pairs — a preview that fits on screen without scrolling
 *   · tap the card → expand to reveal the rest of the visible columns
 *   · uses each column's `options.customBodyRender` when present so
 *     formatted values (links, badges, etc.) render the same way as on
 *     the desktop table
 *
 * Search is client-side over the already-fetched page (matches any visible
 * column's string value, case-insensitive). For deeper search the user
 * still has the desktop view; mobile is for "I need to find this one
 * record I'm thinking of", not analytics.
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
  padding: ${space.md};
`

const SearchInput = styled.input`
  width: 100%;
  font-size: 16px;
  padding: 12px 14px;
  border: 1px solid ${colors.borderDefault};
  border-radius: ${radius.md};
  background: ${colors.white};
  color: ${colors.textPrimary};

  &:focus {
    outline: none;
    border-color: ${colors.ohOrange};
  }
`

const Card = styled.div`
  background: ${colors.white};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${space.md};
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
  cursor: pointer;
`

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: ${space.sm};
  font-size: 14px;
  line-height: 1.4;
  align-items: baseline;
`

const Label = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const Value = styled.span`
  font-size: 14px;
  color: ${colors.textPrimary};
  overflow-wrap: anywhere;
`

const ExpandHint = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  margin-top: ${space.xs};
`

const Empty = styled.div`
  padding: ${space.lg};
  text-align: center;
  color: ${colors.textSecondary};
  font-size: 14px;
`

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${colors.textSecondary};
  padding: 0 ${space.xs};
`

// Best-effort string for the row, used by the search filter. Pulls plain
// data values via dot paths; doesn't try to invoke customBodyRender (some
// render fns expect a full tableMeta argument that we can't fake).
const rowSearchString = (row, visibleColumns) =>
  visibleColumns
    .map((c) => {
      const v = get(row, c.name)
      if (v == null) return ''
      if (typeof v === 'object') {
        // Pull primitive leaves out of nested objects so e.g. user.full_name
        // or { name: 'X' } still matches a search for "X".
        return Object.values(v)
          .filter((x) => typeof x === 'string' || typeof x === 'number')
          .join(' ')
      }
      return String(v)
    })
    .join(' ')
    .toLowerCase()

const renderValue = (col, row) => {
  const raw = get(row, col.name)
  if (col.options && typeof col.options.customBodyRender === 'function') {
    try {
      // We pass empty tableMeta — most renderers just use `value`, which is
      // fine. The ones that depend on tableMeta will fall through to the
      // raw value via the catch.
      return col.options.customBodyRender(raw, {})
    } catch (e) {
      /* fall through to raw rendering */
    }
  }
  if (raw == null || raw === '') return '—'
  if (typeof raw === 'object') {
    // Common shapes in this codebase: { uuid, name } / { first_name, last_name }
    if (raw.name) return raw.name
    if (raw.full_name) return raw.full_name
    if (raw.first_name || raw.last_name)
      return `${raw.first_name ?? ''} ${raw.last_name ?? ''}`.trim()
    return JSON.stringify(raw)
  }
  return String(raw)
}

const MobileEntityCardList = ({ data, columns, label }) => {
  const [query, setQuery] = React.useState('')
  const [expandedId, setExpandedId] = React.useState(null)

  const visibleColumns = React.useMemo(
    () =>
      (columns || []).filter(
        (c) =>
          c &&
          c.label &&
          c.name &&
          (!c.options || c.options.display !== false) &&
          !c.isAction
      ),
    [columns]
  )
  const previewColumns = visibleColumns.slice(0, 4)

  const rows = data?.data ?? []
  const totalCount = data?.count ?? rows.length

  const filtered = React.useMemo(() => {
    if (!query) return rows
    const needle = query.toLowerCase()
    return rows.filter((row) => rowSearchString(row, visibleColumns).includes(needle))
  }, [rows, query, visibleColumns])

  const placeholder =
    typeof label === 'string' ? `Search ${label.toLowerCase()}…` : 'Search…'

  return (
    <Container>
      <SearchInput
        type="search"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="search rows"
      />
      <Meta>
        <span>
          {filtered.length} of {totalCount}
          {query ? ` matching "${query}"` : ''}
        </span>
        {data?.isLoading && <span>loading…</span>}
      </Meta>
      {filtered.length === 0 && !data?.isLoading && (
        <Empty>
          {query
            ? `No rows match "${query}" on this page. Try clearing the search or switching to desktop for the full set.`
            : 'No rows yet.'}
        </Empty>
      )}
      {filtered.map((row, index) => {
        const id = row?.uuid ?? row?.id ?? index
        const isExpanded = expandedId === id
        const colsToShow = isExpanded ? visibleColumns : previewColumns
        const hasMore = visibleColumns.length > previewColumns.length
        return (
          <Card
            key={id}
            onClick={() => setExpandedId(isExpanded ? null : id)}
            role="button"
            tabIndex={0}
          >
            {colsToShow.map((col) => (
              <FieldRow key={col.name + (col.label || '')}>
                <Label>{col.label}</Label>
                <Value>{renderValue(col, row)}</Value>
              </FieldRow>
            ))}
            {hasMore && (
              <ExpandHint>
                {isExpanded
                  ? 'tap to collapse ↑'
                  : `tap for ${visibleColumns.length - previewColumns.length} more fields ↓`}
              </ExpandHint>
            )}
          </Card>
        )
      })}
    </Container>
  )
}

MobileEntityCardList.propTypes = {
  data: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
}

export default MobileEntityCardList
